import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Car, MapPin, Phone, Navigation, Check, Clock, 
  Volume2, VolumeX, ArrowLeft, RefreshCw, Package,
  Timer, CheckCircle2, PlayCircle, Euro, User,
  ExternalLink, Route
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Notification Sound
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkJOPi4J3aF1YXWh0gYuTl5GPh3xxZl1aX2h1g46Wm5iSiH51aWFdYWp3hZCZnpqUi4F4bmVgYGdyfYqUnp6ak4uCeXBnY2Vpdn+MlZ6fnJWNhHx0bGhlanuGj5ifn5yWj4aBenNsaWt3goyWn5+cl5GJgnt1b2xud4OOl5+fnpiSioN9d3JvcnmFj5ifn52Zk4yFgHp2c3V8h5GZn5+dmJONh4J9eXZ4fomSmZ6enJmUjoiDf3x5e4CLlJqenp2alJCKhYF+fH2DjZabnp6dmZWRi4aEgX9/hpCYnJ6enJqWkYyIhYOBhIqTmZyen52bmJOPi4iGhYaLlJqcnp6dnJiUkI2KiIaIjZabnZ6enZyZlZGOi4mIi5CYnJ6enp2cmJWSkI2LioqPlpudnp6enZqXlJGPjYuMkZebnZ6enp2blpSTkI6NjZKYnJ2enp6dm5iVk5GQjo+Um5ydnp6enZuYlpSSkJCRl5ycnZ6enp2bmJaUk5GRk5mbnZ2enp6dm5mXlZOSkpSam5ydnp6enZuZl5aUk5OVm5ydnZ6enp2bmZeWlJSUl5ucnZ2enp6dnJqYl5aVlZebnJ2dnp6enZ2bmpiXlpaYm5ydnZ6enp6dnJqZmJeXmZucnZ2enp6enZybmpmYmJqbnJ2dnp6enp2cm5qZmZmbnJydnZ6enp6dnJuampucnJydnZ6enp6enZyb';

export default function DriverStation({ 
  sessionToken, 
  businessName,
  onBack 
}) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const previousDeliveryCountRef = useRef(0);
  const audioRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.7;
  }, []);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Fetch deliveries
  const fetchDeliveries = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/driver/deliveries`,
        { headers: { 'X-Session-Token': sessionToken } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const newCount = data.deliveries?.length || 0;
        
        // Play sound on new deliveries
        if (newCount > previousDeliveryCountRef.current && previousDeliveryCountRef.current > 0) {
          playNotificationSound();
          toast.success('🔔 Neue Lieferung bereit!', { duration: 5000 });
        }
        previousDeliveryCountRef.current = newCount;
        
        setDeliveries(data.deliveries || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Fetch deliveries error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, playNotificationSound]);

  // Poll for deliveries
  useEffect(() => {
    fetchDeliveries();
    const interval = setInterval(fetchDeliveries, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  // Start delivery
  const startDelivery = async (orderId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/driver/start-delivery?order_id=${orderId}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('🚗 Lieferung gestartet!');
        fetchDeliveries();
      } else {
        toast.error('Fehler beim Starten');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Complete delivery
  const completeDelivery = async (orderId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/driver/complete-delivery?order_id=${orderId}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('✅ Lieferung abgeschlossen!');
        fetchDeliveries();
      } else {
        toast.error('Fehler beim Abschließen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Open Google Maps for navigation
  const openNavigation = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  // Open Google Maps for multiple deliveries (route)
  const openRoute = () => {
    if (selectedDeliveries.length === 0) {
      toast.error('Wählen Sie mindestens eine Lieferung aus');
      return;
    }
    
    const addresses = selectedDeliveries
      .map(id => deliveries.find(d => d.order_id === id)?.address)
      .filter(Boolean);
    
    if (addresses.length === 0) {
      toast.error('Keine Adressen gefunden');
      return;
    }
    
    // First address is destination, rest are waypoints
    const destination = encodeURIComponent(addresses[addresses.length - 1]);
    const waypoints = addresses.slice(0, -1).map(a => encodeURIComponent(a)).join('|');
    
    let url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    
    window.open(url, '_blank');
  };

  // Toggle delivery selection
  const toggleSelection = (orderId) => {
    setSelectedDeliveries(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Calculate time since ready
  const getTimeSinceReady = (timestamp) => {
    if (!timestamp) return '';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 1) return 'Gerade fertig';
    if (diff < 60) return `${diff} Min. bereit`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m bereit`;
  };

  // Get urgency color
  const getUrgencyColor = (timestamp) => {
    if (!timestamp) return 'text-gray-400';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 10) return 'text-green-400';
    if (diff < 20) return 'text-amber-400';
    if (diff < 30) return 'text-orange-400';
    return 'text-red-400';
  };

  // Separate ready and on_the_way deliveries
  const readyDeliveries = deliveries.filter(d => d.status === 'ready');
  const onTheWayDeliveries = deliveries.filter(d => d.status === 'on_the_way');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-600 to-gray-700 p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Car className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Fahrer-Station</h1>
                <p className="text-white/70 text-sm">{businessName}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Delivery Counts */}
            <div className="bg-green-500/20 px-4 py-2 rounded-lg border border-green-500/30">
              <span className="text-green-400 font-bold text-xl">{readyDeliveries.length}</span>
              <span className="text-green-300/70 ml-2">Bereit</span>
            </div>
            <div className="bg-purple-500/20 px-4 py-2 rounded-lg border border-purple-500/30">
              <span className="text-purple-400 font-bold text-xl">{onTheWayDeliveries.length}</span>
              <span className="text-purple-300/70 ml-2">Unterwegs</span>
            </div>
            
            {/* Route Planning Button */}
            {selectedDeliveries.length > 0 && (
              <Button
                onClick={openRoute}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Route className="w-5 h-5 mr-2" />
                Route planen ({selectedDeliveries.length})
              </Button>
            )}
            
            {/* Sound Toggle */}
            <Button
              variant="ghost"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/20"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            {/* Refresh */}
            <Button
              variant="ghost"
              onClick={fetchDeliveries}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-400">Lade Lieferungen...</div>
          </div>
        ) : deliveries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Package className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-xl">Keine Lieferungen verfügbar</p>
            <p className="text-sm mt-2">Neue Lieferungen erscheinen hier automatisch</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Ready Deliveries */}
            {readyDeliveries.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6" />
                  Bereit zur Abholung ({readyDeliveries.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {readyDeliveries.map((delivery) => (
                    <Card 
                      key={delivery.order_id}
                      data-testid={`delivery-${delivery.order_id}`}
                      className={`
                        bg-gray-800/80 border-2 overflow-hidden cursor-pointer
                        ${selectedDeliveries.includes(delivery.order_id) 
                          ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                          : 'border-green-500/30 hover:border-green-500/50'}
                      `}
                      onClick={() => toggleSelection(delivery.order_id)}
                    >
                      <CardHeader className="pb-2 bg-green-500/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg text-white">
                              #{delivery.order_number}
                            </CardTitle>
                            <div className={`flex items-center gap-1 text-sm ${getUrgencyColor(delivery.ready_at)}`}>
                              <Timer className="w-4 h-4" />
                              {getTimeSinceReady(delivery.ready_at)}
                            </div>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500">
                            Bereit
                          </Badge>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-3 space-y-3">
                        {/* Customer Info */}
                        <div className="flex items-center gap-2 text-white">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{delivery.customer_name || 'Unbekannt'}</span>
                        </div>
                        
                        {/* Phone */}
                        {delivery.customer_phone && (
                          <a 
                            href={`tel:${delivery.customer_phone}`}
                            className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="w-4 h-4" />
                            <span>{delivery.customer_phone}</span>
                          </a>
                        )}
                        
                        {/* Address */}
                        <div className="flex items-start gap-2 text-gray-300">
                          <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                          <span className="text-sm">{delivery.address || 'Keine Adresse'}</span>
                        </div>
                        
                        {/* Total & Payment */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                          <div className="flex items-center gap-1 text-amber-400 font-bold">
                            <Euro className="w-4 h-4" />
                            {delivery.total?.toFixed(2)}
                          </div>
                          <Badge variant="outline" className={
                            delivery.paid 
                              ? 'text-green-400 border-green-400' 
                              : 'text-amber-400 border-amber-400'
                          }>
                            {delivery.paid ? 'Bezahlt' : delivery.payment_method === 'cash' ? 'Bar' : 'Karte'}
                          </Badge>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openNavigation(delivery.address);
                            }}
                            className="flex-1 border-blue-500 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Navigation
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              startDelivery(delivery.order_id);
                            }}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          >
                            <PlayCircle className="w-4 h-4 mr-1" />
                            Starten
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {/* On The Way Deliveries */}
            {onTheWayDeliveries.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
                  <Car className="w-6 h-6" />
                  Unterwegs ({onTheWayDeliveries.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {onTheWayDeliveries.map((delivery) => (
                    <Card 
                      key={delivery.order_id}
                      data-testid={`delivery-active-${delivery.order_id}`}
                      className="bg-gray-800/80 border-2 border-purple-500/30 overflow-hidden"
                    >
                      <CardHeader className="pb-2 bg-purple-500/10">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg text-white">
                            #{delivery.order_number}
                          </CardTitle>
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500">
                            Unterwegs
                          </Badge>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-3 space-y-3">
                        {/* Customer Info */}
                        <div className="flex items-center gap-2 text-white">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{delivery.customer_name || 'Unbekannt'}</span>
                        </div>
                        
                        {/* Phone */}
                        {delivery.customer_phone && (
                          <a 
                            href={`tel:${delivery.customer_phone}`}
                            className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                          >
                            <Phone className="w-4 h-4" />
                            <span>{delivery.customer_phone}</span>
                          </a>
                        )}
                        
                        {/* Address */}
                        <div className="flex items-start gap-2 text-gray-300">
                          <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                          <span className="text-sm">{delivery.address || 'Keine Adresse'}</span>
                        </div>
                        
                        {/* Total */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                          <div className="flex items-center gap-1 text-amber-400 font-bold">
                            <Euro className="w-4 h-4" />
                            {delivery.total?.toFixed(2)}
                          </div>
                          <Badge variant="outline" className={
                            delivery.paid 
                              ? 'text-green-400 border-green-400' 
                              : 'text-amber-400 border-amber-400'
                          }>
                            {delivery.paid ? 'Bezahlt' : 'Kassieren'}
                          </Badge>
                        </div>
                        
                        {/* Complete Button */}
                        <Button
                          onClick={() => completeDelivery(delivery.order_id)}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Lieferung abschließen
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800/80 backdrop-blur-sm p-2 text-center text-gray-400 text-sm">
        Letzte Aktualisierung: {lastUpdate?.toLocaleTimeString('de-DE') || '-'}
        <span className="ml-4">• Google Maps für Navigation</span>
      </div>
    </div>
  );
}
